import Link from "next/link";
import cx from "classnames";
import Icon from "@/atoms/Icon";
import Card from "@/atoms/account/Card";
import Divider from "@/atoms/account/Divider";
import { m } from "framer-motion";
import { LINKS } from "globalConfig";
import styled from "styled-components";
import { hexToRGBTriplet } from "utils/styles";
import partition from "lodash/partition";
import { KUSTOMER_Z_INDEX } from "theme";
import { MouseEventHandler, ReactNode, useContext } from "react";
import { AuthContext } from "contexts/AuthProvider";
import { Subscription as SubscriptionType } from "lib/api/subscription";
import {
  AFFILIATE_MENU,
  MenuItem,
  MenuItemList,
  USER_ACCOUNT_MENU,
} from "components/Layout/Navbar/config";

const StyledPresenceContainer = styled(m.div)`
  display: flow-root;
  ${({ theme }) => theme.media["md"]} {
    opacity: 1 !important;
    height: auto !important;
    visibility: visible !important;
  }
`;

const PresenceContainer = ({
  present,
  children,
  className,
}: {
  present: boolean;
  children: ReactNode;
  className?: string;
}) => (
  <StyledPresenceContainer
    className={cx("presence-container", className)}
    layout
    initial={false}
    animate={
      present
        ? { opacity: 1, height: "auto", visibility: "visible" }
        : { opacity: 0, height: 0, visibility: "hidden" }
    }
    transition={{ duration: 0.2 }}
  >
    {children}
  </StyledPresenceContainer>
);

const StyledSidebarMenuItem = styled.a`
  display: block;
  text-decoration: none;
  border-radius: 6px;
  margin: 0.25rem 0;
  padding: 0.5rem 0.75rem;
  color: ${({ theme }) => theme.colors.textGray.default};
  cursor: pointer;
  &.active {
    background: rgba(89, 110, 202, 0.1);
    color: ${({ theme }) => theme.colors.perriwinkle};
  }
`;

const SidebarMenuItem = ({
  active,
  href,
  children,
  className,
}: { active: boolean } & React.HTMLProps<typeof StyledSidebarMenuItem>) => (
  <Link href={href || ""} passHref>
    <StyledSidebarMenuItem className={cx({ active }, className)}>
      {children}
    </StyledSidebarMenuItem>
  </Link>
);

interface SidebarMenuProps {
  items: MenuItemList;
  expanded: boolean;
  activePath: string;
}
const SidebarMenuPrimary = ({
  items,
  expanded,
  activePath,
}: SidebarMenuProps) => {
  return (
    <>
      {items.map((item) => {
        if (!item) {
          return null;
        }
        const isActive = activePath.startsWith(item.url);
        return (
          <PresenceContainer present={expanded || isActive} key={item.label}>
            <SidebarMenuItem active={isActive} href={item.url}>
              <div className={"flex items-center font-bold"}>
                <Icon name={item.icon} size={"1.5rem"} className={"mr-3"} />
                {item.label}
              </div>
            </SidebarMenuItem>
          </PresenceContainer>
        );
      })}
    </>
  );
};

const Subscription = styled(SidebarMenuItem)`
  .sub-heading {
    display: flex;
    justify-content: space-between;
    align-items: center;

    span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-weight: 700;
      max-width: 80vw;
      ${({ theme }) => theme.media["md"]} {
        max-width: 180px;
      }
    }
  }

  .date-badge {
    font-size: 10px;
    line-height: 12px;
    padding: 4px 6px;
    font-weight: 700;
    background-color: #f7f6f3;
    border-radius: 4px;
    color: ${({ theme }) => theme.colors.perriwinkle};
    margin: 0.25rem 0.75rem 0 0;

    &.active {
      background-color: rgba(89, 110, 202, 0.1);
    }
  }

  .date-badge.canceled {
    &,
    span {
      color: ${({ theme }) => theme.colors.error};
    }
    background: rgba(
      ${({ theme }) => hexToRGBTriplet(theme.colors.error)},
      0.1
    );
  }

  .date-badge.canceled.active {
    background-color: rgba(255, 83, 93, 0.1);
  }
`;
interface NameAndChevProps {
  $autoRenews: boolean;
  $isActive: boolean;
}
const NameAndChev = styled.div<NameAndChevProps>`
  color: ${({ $autoRenews, $isActive, theme }) => {
    if ($isActive) return theme.colors.perriwinkle;
    if ($autoRenews) return theme.colors.textGray.default;
    return theme.colors["warm-gray-3"];
  }};
`;

const SidebarMenuSubscriptions = ({
  subscriptions = [],
  expanded,
  activePath,
}: {
  subscriptions: SubscriptionType[];
  expanded: boolean;
  activePath: string;
}) => {
  const BASE_URL = "/account/subscription";

  return (
    <>
      {subscriptions.map((sub) => {
        const subUrl = `${BASE_URL}/${sub.id}/`;
        const isActive = activePath.startsWith(subUrl);
        const autoRenews = Boolean(sub.renewalDate);

        return (
          <PresenceContainer present={expanded || isActive} key={subUrl}>
            <Subscription active={isActive} href={subUrl} key={subUrl}>
              <NameAndChev
                $autoRenews={autoRenews}
                $isActive={isActive}
                className={"sub-heading"}
              >
                <span>{sub.childName}</span>
                <Icon name={"chevron-right"} size={"0.65rem"} />
              </NameAndChev>
              <PresenceContainer present={expanded}>
                {!autoRenews && (
                  <span
                    className={cx("date-badge text-error", {
                      active: isActive,
                    })}
                  >
                    <span className={"text-textGray-lighter"}>Inactive</span>
                  </span>
                )}
              </PresenceContainer>
            </Subscription>
          </PresenceContainer>
        );
      })}
    </>
  );
};

// One less than Kustomer's zIndex.
// So that Sidebar doesn't overlap Kustomer's
// own open chat Modal.
const zIndex = KUSTOMER_Z_INDEX - 1;

const StyledSidebar = styled(Card)`
  ${({ theme }) => theme.media["max:md"]} {
    position: absolute;
    width: 100vw;
    top: 0;
    left: 0;
    z-index: ${zIndex};
    border-radius: 0 !important;
    padding: 0.5rem 1rem;
  }

  ${({ theme }) => theme.media["md"]} {
    position: sticky;
    top: 1rem;
  }

  .content-wrapper {
    display: grid;

    ${({ theme }) => theme.media["max:md"]} {
      grid-template-columns: auto 2rem;
      grid-column-gap: 1rem;
    }
  }
`;
export interface SidebarProps {
  subscriptions: SubscriptionType[];
  expanded: boolean;
  toggleMenu: MouseEventHandler<HTMLButtonElement>;
  activePath: string;
  isAffiliate?: boolean;
}
const Sidebar = ({
  subscriptions = [],
  expanded,
  toggleMenu,
  activePath,
  isAffiliate,
}: SidebarProps) => {
  const sortedSubs = [...subscriptions].sort((a) => (a.renewalDate ? -1 : 1));
  const [giftSubs, regularSubs] = partition(sortedSubs, (s) =>
    s.plan?.slug.startsWith("gift")
  );

  const { openLogoutModal } = useContext(AuthContext);
  let items = [...USER_ACCOUNT_MENU];
  if (isAffiliate) {
    items = [...items, ...AFFILIATE_MENU];
  }

  return (
    <StyledSidebar id={"account-sidebar"}>
      <div className={"content-wrapper"}>
        <div>
          <SidebarMenuPrimary
            items={items}
            expanded={expanded}
            activePath={activePath}
          />
          {Boolean(regularSubs.length) && (
            <>
              <PresenceContainer present={expanded}>
                <>
                  <Divider className={"my-6"} />
                  <div className={"text-xxs uppercase tracking-wider px-3"}>
                    Subscription Settings
                  </div>
                </>
              </PresenceContainer>
              <SidebarMenuSubscriptions
                subscriptions={regularSubs}
                expanded={expanded}
                activePath={activePath}
              />
            </>
          )}
          {Boolean(giftSubs.length) && (
            <>
              <PresenceContainer present={expanded}>
                <div className={"text-xxs uppercase tracking-wider px-3 pt-4"}>
                  Gifts
                </div>
              </PresenceContainer>
              <SidebarMenuSubscriptions
                subscriptions={giftSubs}
                expanded={expanded}
                activePath={activePath}
              />
            </>
          )}
          <PresenceContainer present={expanded}>
            <Link href={LINKS.subscribe}>
              <a className={"font-bold px-3 py-4 block"}>+ Add subscription</a>
            </Link>
          </PresenceContainer>
          <div className={"md:hidden"}>
            <PresenceContainer present={expanded}>
              <>
                <Divider className={"mt-4"} />
                <button
                  onClick={openLogoutModal}
                  className={"flex items-center font-bold text-error px-3 py-4"}
                >
                  <Icon name={"logout"} size={"1.5rem"} className={"mr-3"} />
                  Sign Out
                </button>
              </>
            </PresenceContainer>
          </div>
        </div>
        <button
          onClick={toggleMenu}
          aria-label={"toggle menu"}
          className={"text-textGray-lighter self-start pt-3 md:hidden"}
        >
          <Icon
            name={"chevron-right"}
            size={"1rem"}
            className={`transform ${expanded ? "-rotate-90" : "rotate-90"}`}
          />
        </button>
      </div>
    </StyledSidebar>
  );
};

export default Sidebar;
